import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { createPatient, getPatient, updatePatient } from '../services/api';
import { PrimaryButton } from '../components/PrimaryButton';

interface PatientForm {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  address: string;
  nit: string;
  occupation: string;
  age: string;
  gender: string;
  history_hypertension: boolean;
  history_diabetes: boolean;
  history_other: string;
}

export const PatientCreation = () => {
  const navigate = useNavigate();
  const { patient_id } = useParams();
  const isEditMode = Boolean(patient_id);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<PatientForm>({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    address: '',
    nit: '',
    occupation: '',
    age: '',
    gender: 'M',
    history_hypertension: false,
    history_diabetes: false,
    history_other: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (patient_id) {
      getPatient(patient_id)
        .then((patient) => {
          setFormData({
            first_name: patient.first_name || '',
            last_name: patient.last_name || '',
            phone: patient.phone || '',
            email: patient.email || '',
            address: patient.address || '',
            nit: patient.nit || '',
            occupation: patient.occupation || '',
            age: patient.age?.toString() || '',
            gender: patient.gender || 'M',
            history_hypertension: patient.history_hypertension || false,
            history_diabetes: patient.history_diabetes || false,
            history_other: patient.history_other || '',
          });
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'Error al cargar paciente');
        });
    }
  }, [patient_id]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'El nombre es requerido';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'El apellido es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const inputElement = e.target as HTMLInputElement;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? inputElement.checked : value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleGenderChange = (gender: string) => {
    setFormData(prev => ({
      ...prev,
      gender
    }));
  };

  const toggleMedicalFlag = (flag: 'history_hypertension' | 'history_diabetes') => {
    setFormData(prev => ({
      ...prev,
      [flag]: !prev[flag]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const patientData = {
        ...formData,
        phone: formData.phone || '',
      };

      if (isEditMode && patient_id) {
        await updatePatient(patient_id, {
          ...patientData,
          age: patientData.age ? parseInt(patientData.age) : undefined,
        });
        navigate(`/history/${patient_id}`);
      } else {
        const response = await createPatient(patientData);
        navigate(`/clinical-chart/${response.data.id}/new`, { 
          state: { patientId: response.data.id } 
        });
      }
    } catch (err) {
      console.error('Error creating patient:', err);
      setError(err instanceof Error ? err.message : isEditMode ? 'Error al actualizar el paciente' : 'Error al crear el paciente');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode && patient_id) {
      navigate(`/history/${patient_id}`);
    } else {
      navigate('/home');
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-[#f6f7f8]">
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        <Navbar />
      </div>

      <main className="flex flex-1 flex-col items-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="w-full max-w-4xl space-y-8 pb-32">
          {/* Header */}
          <header>
            <h1 className="text-2xl sm:text-4xl font-black text-slate-900 leading-tight tracking-tight">
              {isEditMode ? 'Editar Paciente' : 'Registro de Nuevo Paciente'}
            </h1>
          </header>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Error Alert */}
            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-4">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {/* Personal Information Section */}
            <div className="rounded-xl bg-white shadow-sm p-6 md:p-8">
              <h2 className="text-[22px] font-bold text-slate-900 leading-tight mb-6">
                Información Personal
              </h2>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Names */}
                <div className="flex flex-col">
                  <label className="text-base font-bold text-slate-800 pb-2">
                    Nombres <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    placeholder="Nombres"
                    className={`h-14 px-5 rounded-xl border text-lg font-medium bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
                      errors.first_name
                        ? 'border-red-500'
                        : 'border-slate-300'
                    }`}
                  />
                  {errors.first_name && (
                    <span className="mt-1 text-sm text-red-500">{errors.first_name}</span>
                  )}
                </div>

                {/* Last Name */}
                <div className="flex flex-col">
                  <label className="text-base font-bold text-slate-800 pb-2">
                    Apellidos <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    placeholder="Apellidos"
                    className={`h-14 px-5 rounded-xl border text-lg font-medium bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
                      errors.last_name
                        ? 'border-red-500'
                        : 'border-slate-300'
                    }`}
                  />
                  {errors.last_name && (
                    <span className="mt-1 text-sm text-red-500">{errors.last_name}</span>
                  )}
                </div>

                {/* Phone */}
                <div className="flex flex-col">
                  <label className="text-base font-medium text-slate-800 pb-2">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Teléfono"
                    className="h-14 px-5 rounded-xl border border-slate-300 text-lg font-medium bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  />
                </div>

                {/* Email */}
                <div className="flex flex-col">
                  <label className="text-base font-medium text-slate-800 pb-2">
                    Correo Electrónico
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Correo Electrónico"
                    className="h-14 px-5 rounded-xl border border-slate-300 text-lg font-medium bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  />
                </div>

                {/* Address */}
                <div className="sm:col-span-2 flex flex-col">
                  <label className="text-base font-medium text-slate-800 pb-2">
                    Dirección
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Dirección"
                    className="h-14 px-5 rounded-xl border border-slate-300 text-lg font-medium bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  />
                </div>

                {/* NIT */}
                <div className="flex flex-col">
                  <label className="text-base font-medium text-slate-800 pb-2">
                    NIT
                  </label>
                  <input
                    type="text"
                    name="nit"
                    value={formData.nit}
                    onChange={handleInputChange}
                    placeholder="Número de Identificación Tributaria"
                    className="h-14 px-5 rounded-xl border border-slate-300 text-lg font-medium bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  />
                </div>

                {/* Occupation */}
                <div className="flex flex-col">
                  <label className="text-base font-medium text-slate-800 pb-2">
                    Ocupación
                  </label>
                  <input
                    type="text"
                    name="occupation"
                    value={formData.occupation}
                    onChange={handleInputChange}
                    placeholder="Ocupación"
                    className="h-14 px-5 rounded-xl border border-slate-300 text-lg font-medium bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  />
                </div>

                {/* Age */}
                <div className="flex flex-col">
                  <label className="text-base font-medium text-slate-800 pb-2">
                    Edad
                  </label>
                  <input
                    type="number"
                    pattern="[0-9]*"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    placeholder="Edad"
                    min="0"
                    max="150"
                    className="h-14 px-5 rounded-xl border border-slate-300 text-lg font-medium bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  />
                </div>

                {/* Gender */}
                <div className="flex flex-col">
                  <label className="text-base font-medium text-slate-800 pb-2">
                    Género
                  </label>
                  <div className="grid grid-cols-3 gap-2 p-1 bg-white rounded-lg" role="radiogroup">
                    {[
                      { value: 'M', label: 'Masculino' },
                      { value: 'F', label: 'Femenino' },
                      { value: 'O', label: 'Otro' },
                    ].map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleGenderChange(option.value)}
                        className={`h-12 text-base font-semibold rounded-md transition-all ${
                          formData.gender === option.value
                            ? 'bg-white text-primary shadow-sm ring-2 ring-primary'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                        role="radio"
                        aria-checked={formData.gender === option.value}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Medical History Section */}
            <div className="rounded-xl bg-white shadow-sm p-6 md:p-8">
              <h2 className="text-[22px] font-bold text-slate-900 leading-tight mb-6">
                Antecedentes Médicos (Crónicos)
              </h2>

              <div className="space-y-6">
                {/* Medical Flags */}
                <div className="flex flex-wrap gap-4">
                  <button
                    type="button"
                    onClick={() => toggleMedicalFlag('history_hypertension')}
                    className={`h-14 px-6 text-lg font-semibold rounded-xl transition-all ${
                      formData.history_hypertension
                        ? 'bg-primary/10 text-primary ring-2 ring-inset ring-primary'
                        : 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-200'
                    }`}
                  >
                    Hipertensión
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleMedicalFlag('history_diabetes')}
                    className={`h-14 px-6 text-lg font-semibold rounded-xl transition-all ${
                      formData.history_diabetes
                        ? 'bg-primary/10 text-primary ring-2 ring-inset ring-primary'
                        : 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-200'
                    }`}
                  >
                    Diabetes
                  </button>
                </div>

                {/* Other Medical History */}
                <div className="flex flex-col">
                  <label className="text-base font-medium text-slate-800 pb-2">
                    Otros Antecedentes
                  </label>
                  <textarea
                    name="history_other"
                    value={formData.history_other}
                    onChange={handleInputChange}
                    placeholder="Especifique otros antecedentes médicos"
                    rows={3}
                    className="px-5 py-3 rounded-xl border border-slate-300 text-lg font-medium bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors resize-none"
                  />
                </div>
              </div>
            </div>

          </form>
        </div>
      </main>

      {/* Sticky Footer */}
      <footer className="fixed bottom-0 left-0 right-0 z-10 border-t border-slate-200/80 bg-white/80 backdrop-blur-sm">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 py-4">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isLoading}
              className="w-full sm:w-auto h-14 px-8 rounded-xl text-lg font-semibold text-slate-700 bg-slate-200/80 hover:bg-slate-300/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancelar
            </button>
            <PrimaryButton
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full sm:w-auto h-14 px-8 text-lg"
            >
              {isLoading
                ? (isEditMode ? 'Guardando...' : 'Creando...')
                : (isEditMode ? 'Guardar Cambios' : 'Crear y Comenzar Examen')}
            </PrimaryButton>
          </div>
        </div>
      </footer>
    </div>
  );
};
